import type {
  AskLookalikePollDto,
  AskResearchRoomDto,
  ResearchAnalysisResult,
  ResearchAnalysisTrigger,
  ResearchQuestionShapeResult,
  ResearchReportView,
  ResearchRoomEvent,
  ResearchRoomFixesResult,
  ResearchRoomLookup,
  ResearchRoomMessage,
  ResearchRoomView,
  ResearchSessionStatus,
} from '@repo/contracts';

import { getSessionToken } from '../auth/session-token';
import { useScopeStore } from '../store/scope-store';
import { ApiError, apiFetch, failure, streamOrigin } from './client';
import { parseSseFrames } from './sse-frames';

/**
 * Research rooms: a moderated chat between the researcher, Floto and the
 * project's personas, about one capture. Type-only imports from
 * `@repo/contracts`, for the reason `canvas-personas.ts` gives.
 */

export const roomKeys = {
  all: ['research-rooms'] as const,
  /** The room open on a capture — or null when there is none yet. */
  forCapture: (captureId: string) =>
    [...roomKeys.all, 'capture', captureId] as const,
  /** A session's chat — its newest room — or null when there is none yet.
   *  Read only to learn the chat's context; the room itself is read and
   *  written under `forCapture`. */
  forSession: (sessionId: string) =>
    [...roomKeys.all, 'session', sessionId] as const,
};

/** Where research stands in each session — per project, or everywhere. */
export const researchStatusKeys = {
  all: ['research-status'] as const,
  forProject: (projectId: string) =>
    [...researchStatusKeys.all, 'project', projectId] as const,
  everywhere: () => [...researchStatusKeys.all, 'everywhere'] as const,
};

export function getResearchStatus(
  projectId?: string,
): Promise<ResearchSessionStatus[]> {
  return apiFetch<ResearchSessionStatus[]>(
    `/canvas/research-rooms/status${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`,
  );
}

/** A room's final report, with everything its page draws. */
export const reportKeys = {
  all: ['research-reports'] as const,
  forRoom: (roomId: string) => [...reportKeys.all, roomId] as const,
};

export function getReport(roomId: string): Promise<ResearchReportView> {
  return apiFetch<ResearchReportView>(
    `/canvas/research-rooms/${encodeURIComponent(roomId)}/report`,
  );
}

/** The room on a capture, or null when none has been opened yet. */
export async function getRoomForCapture(
  captureId: string,
): Promise<ResearchRoomView | null> {
  const { view } = await apiFetch<ResearchRoomLookup>(
    `/canvas/research-rooms?captureId=${encodeURIComponent(captureId)}`,
  );
  return view;
}

/** A session's chat — its newest room — or null when none has been opened. */
export async function getRoomForSession(
  sessionId: string,
): Promise<ResearchRoomView | null> {
  const { view } = await apiFetch<ResearchRoomLookup>(
    `/canvas/research-rooms?sessionId=${encodeURIComponent(sessionId)}`,
  );
  return view;
}

export function createRoom(captureId: string): Promise<ResearchRoomView> {
  return apiFetch<ResearchRoomView>('/canvas/research-rooms', {
    body: JSON.stringify({ captureId }),
    method: 'POST',
  });
}

/** Re-seats a room with exactly these people. 409 while it is answering. */
export function updateRoomPersonas(
  roomId: string,
  personaIds: string[],
): Promise<ResearchRoomView> {
  return apiFetch<ResearchRoomView>(
    `/canvas/research-rooms/${roomId}/personas`,
    {
      body: JSON.stringify({ personaIds }),
      method: 'PATCH',
    },
  );
}

/** Chooses the screens a room is about, in order, its first screen first —
 *  or, with `auto`, every screen on its board. 409 while it is answering. */
export function updateRoomContext(
  roomId: string,
  captureIds: string[],
  /** Back to every screen on the board. */
  options: { auto?: boolean } = {},
): Promise<ResearchRoomView> {
  return apiFetch<ResearchRoomView>(
    `/canvas/research-rooms/${roomId}/context`,
    {
      body: JSON.stringify({ captureIds, ...options }),
      method: 'PATCH',
    },
  );
}

/** What shape a question being typed is — open, a poll, a rating. */
export function classifyQuestion(
  text: string,
): Promise<ResearchQuestionShapeResult> {
  return apiFetch<ResearchQuestionShapeResult>(
    '/canvas/research-rooms/classify-question',
    { body: JSON.stringify({ text }), method: 'POST' },
  );
}

/**
 * Asks the room, calling `onEvent` for each frame of the round as it happens.
 * Resolves once the round is over.
 *
 * Streamed, so it goes through `streamOrigin` rather than `apiFetch`'s origin
 * — Firebase Hosting buffers a streamed response whole. Never retried: a
 * retry would ask the question twice. If the stream drops mid-round the round
 * still finishes server-side, and the room's live query shows the result.
 */
export function askRoom(
  roomId: string,
  dto: AskResearchRoomDto,
  onEvent: (event: ResearchRoomEvent) => void,
): Promise<void> {
  return streamRoom(
    `/canvas/research-rooms/${encodeURIComponent(roomId)}/messages`,
    dto,
    onEvent,
    'The room could not take that question',
  );
}

/**
 * A yes/no question, asked of one persona's lookalike population — outside
 * the room's own turns, so a plain request/response rather than a stream.
 */
export function askLookalikePoll(
  roomId: string,
  dto: AskLookalikePollDto,
): Promise<ResearchRoomMessage> {
  return apiFetch<ResearchRoomMessage>(
    `/canvas/research-rooms/${encodeURIComponent(roomId)}/lookalike-poll`,
    { body: JSON.stringify(dto), method: 'POST' },
  );
}

/** Opens a re-test of a room's questions on another screen. */
export function retestRoom(
  roomId: string,
  captureId: string,
): Promise<ResearchRoomView> {
  return apiFetch<ResearchRoomView>(
    `/canvas/research-rooms/${encodeURIComponent(roomId)}/retest`,
    { body: JSON.stringify({ captureId }), method: 'POST' },
  );
}

/** Runs a re-test: every question again, streamed, then the comparison. */
export function replayRoom(
  roomId: string,
  onEvent: (event: ResearchRoomEvent) => void,
): Promise<void> {
  return streamRoom(
    `/canvas/research-rooms/${encodeURIComponent(roomId)}/replay`,
    {},
    onEvent,
    'The re-test could not start',
  );
}

/**
 * Ends the session: Floto's closing summary, then the final report — the
 * report arrives as `report` frames, pending and then written.
 */
export function endRoom(
  roomId: string,
  onEvent: (event: ResearchRoomEvent) => void,
): Promise<void> {
  return streamRoom(
    `/canvas/research-rooms/${encodeURIComponent(roomId)}/end`,
    {},
    onEvent,
    'The session could not end',
  );
}

/** Writes an ended session's report again. */
export function writeReport(
  roomId: string,
  onEvent: (event: ResearchRoomEvent) => void,
): Promise<void> {
  return streamRoom(
    `/canvas/research-rooms/${encodeURIComponent(roomId)}/report`,
    {},
    onEvent,
    'The report could not be written',
  );
}

/** Makes an ended session's report into a presentation deck. The last
 *  `deck` frame carries a link to download it. */
export function buildDeck(
  roomId: string,
  onEvent: (event: ResearchRoomEvent) => void,
): Promise<void> {
  return streamRoom(
    `/canvas/research-rooms/${encodeURIComponent(roomId)}/deck`,
    {},
    onEvent,
    'The deck could not be made',
  );
}

/**
 * A POST that answers with an event stream, read frame by frame.
 *
 * Streamed, so it goes through `streamOrigin` rather than `apiFetch`'s origin
 * — Firebase Hosting buffers a streamed response whole. Never retried: a
 * retry would ask again. If the stream drops, the work still finishes
 * server-side and the room's live query shows the result.
 */
async function streamRoom(
  path: string,
  body: unknown,
  onEvent: (event: ResearchRoomEvent) => void,
  fallback: string,
): Promise<void> {
  const token = getSessionToken();
  const teamId = useScopeStore.getState().selectedTeamId;

  const response = await fetch(`${streamOrigin}/api${path}`, {
    body: JSON.stringify(body),
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(teamId ? { 'x-team-id': teamId } : {}),
    },
    method: 'POST',
  });

  if (!response.ok || !response.body) {
    // The code and details travel with it — `INSUFFICIENT_CREDITS` is a
    // refusal the composer explains, not a message it shows.
    const refusal = await failure(response, path, fallback);
    throw new ApiError(
      refusal.message,
      response.status,
      refusal.code,
      refusal.details,
    );
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += value;
    const { frames, rest } = parseSseFrames(buffer);
    buffer = rest;

    for (const { data, event } of frames) {
      if (event === 'ping' || !data) continue;
      onEvent(JSON.parse(data) as ResearchRoomEvent);
    }
  }
}

/** Floto's write-up of the room for the board — after a milestone round, or
 *  because the researcher asked. */
export function analyseRoom(
  roomId: string,
  trigger: ResearchAnalysisTrigger,
): Promise<ResearchAnalysisResult> {
  return apiFetch<ResearchAnalysisResult>(
    `/canvas/research-rooms/${roomId}/analysis`,
    { body: JSON.stringify({ trigger }), method: 'POST' },
  );
}

/** Catches the fix list up with a room opened before it kept one. Free, and
 *  a no-op for a room already caught up. */
export function catchUpRoomFixes(
  roomId: string,
): Promise<ResearchRoomFixesResult> {
  return apiFetch<ResearchRoomFixesResult>(
    `/canvas/research-rooms/${roomId}/fixes`,
    { method: 'POST' },
  );
}
